import type { MetricsResult, TimeRange } from '@nx/state';
import { opsApi } from '@nx/state';
import { isNullish } from '@nx/stdlib';
import { useEffect, useState } from 'react';

import { dateToUnixSeconds } from '../../shared/ui-helpers';
import type { ChartType, ChartWidgetPartialProps } from '../charts/fullstack-chart-props';
import type { MultiChartProps } from '../charts/types';
import type { MetricSource } from '../shared/types';

export const useMetricsQuery = ({
  metricSource,
  timeRange,
  chartProps,
  chartType,
  seriesNameMapper,
  active = true,
}: {
  metricSource: MetricSource | undefined;
  timeRange: TimeRange;
  chartProps: MultiChartProps[];
  chartType: ChartType;
  seriesNameMapper?: (props: ChartWidgetPartialProps) => string;
  active?: boolean;
}): MetricsResult => {
  const [processedMetrics, setProcessedMetrics] = useState<MetricsResult['metrics']>(null);
  const [isLoading, setIsLoading] = useState<MetricsResult['isLoading']>(false);
  const [error, setError] = useState<MetricsResult['error']>(undefined);

  const startTime = dateToUnixSeconds(timeRange.startTime);
  const endTime = dateToUnixSeconds(timeRange.endTime);

  const hasAlreadyFetched = processedMetrics !== null;
  const [fetchMetrics] = opsApi.endpoints.getMetrics.useLazyQuery();

  useEffect(() => {
    if (!metricSource) {
      return;
    }

    const shouldDoFirstFetch = active && !hasAlreadyFetched;
    if (!shouldDoFirstFetch && !hasAlreadyFetched) {
      return;
    }

    if (!active) {
      return;
    }

    const resolveMetrics = async (): Promise<void> => {
      setIsLoading(true);
      setError(undefined);
      try {
        const request = metricSource.createMetricsRequest(startTime, endTime, chartType, chartProps);
        const metrics = await fetchMetrics(request, true).unwrap();
        setProcessedMetrics(metricSource.mapMetrics(metrics, chartProps, seriesNameMapper));
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        setError(e as MetricsResult['error']);
      } finally {
        setIsLoading(false);
      }
    };

    void resolveMetrics().then().catch();
  }, [
    active,
    chartType,
    chartProps,
    endTime,
    hasAlreadyFetched,
    metricSource,
    seriesNameMapper,
    startTime,
    fetchMetrics,
  ]);

  return { isLoading, error, metrics: processedMetrics };
};

export const useMetricsMetadata = (selectedProjectId: string | undefined) => {
  const { data: metricsMetadata, isError } = opsApi.endpoints.getMetricsMetadata.useQuery(selectedProjectId ?? '', {
    skip: isNullish(selectedProjectId),
  });

  return { metricsMetadata, isError };
};
